import React, { useState } from "react";
import { Box, Stack, Typography, Paper, IconButton, Collapse, Button, Chip } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { QuizStyledCard } from "../../shared/QuizStyledCard";
import { COLORS } from "../constants";
import { formatDate, formatPrice } from "../helpers";
import { extractLineItemsWithImages, groupLineItems } from "../../../utils/lineItems";
import type { AddressData, SubscriptionData, ModalType } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  upcomingCharges: any[];
  addresses: AddressData[];
  products: any[];
  subscriptions: SubscriptionData[];
  onOpenModal: (type: ModalType, sub?: SubscriptionData) => void;
}

function normalizeCharge(raw: any): any {
  // Handle nested shape variations
  const charge = raw?.charge || raw;
  return {
    id: charge.id,
    scheduled_at: charge.scheduled_at || charge.date || charge.next_charge_scheduled_at,
    status: charge.status || "queued",
    total_price: charge.total_price || charge.subtotal_price || "0",
    line_items: charge.line_items || [],
    address_id: charge.address_id,
  };
}

export function UpcomingDeliveriesSection({ upcomingCharges, addresses, products, subscriptions, onOpenModal }: Props) {
  const [show, setShow] = useState(true);

  const normalized = upcomingCharges.map(normalizeCharge);

  const statusColor = (status: string) => {
    const s = status?.toLowerCase();
    if (s === "queued") return { bg: COLORS.blueLight, color: COLORS.blue };
    if (s === "skipped") return { bg: COLORS.orangeBg, color: COLORS.orangeText };
    if (s === "cancelled") return { bg: COLORS.redLight, color: COLORS.red };
    return { bg: QUIZ.color.cream2, color: COLORS.textMuted };
  };

  const getAddressLabel = (addressId: string) => {
    const addr = addresses.find(a => String(a.id) === String(addressId));
    if (!addr) return null;
    return `${addr.address1}, ${addr.city}`;
  };

  return (
    <QuizStyledCard>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setShow(!show)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocalShippingIcon sx={{ color: COLORS.brown }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>
            Upcoming Deliveries ({normalized.length})
          </Typography>
        </Box>
        <IconButton size="small">
          {show ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      <Collapse in={show} timeout="auto" unmountOnExit>
        {normalized.length === 0 ? (
          <Typography sx={{ mt: 2, fontSize: 13, color: COLORS.textMuted }}>No upcoming deliveries</Typography>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {normalized.map((charge: any, cIdx: number) => {
              const lineItems = extractLineItemsWithImages(charge, products, subscriptions);
              const grouped = groupLineItems(lineItems);
              const { bg, color } = statusColor(charge.status);
              const total = parseFloat(charge.total_price || "0");
              const addrLabel = charge.address_id ? getAddressLabel(charge.address_id) : null;

              return (
                <Paper
                  key={charge.id || cIdx}
                  sx={{ p: 2, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}` }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: COLORS.brownDeep }}>
                          {formatDate(charge.scheduled_at)}
                        </Typography>
                        <Chip
                          label={charge.status}
                          size="small"
                          sx={{ bgcolor: bg, color, fontWeight: 600, fontSize: 11, height: 22 }}
                        />
                      </Box>
                      {addrLabel && (
                        <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>
                          {addrLabel}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>
                        {formatPrice(total)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Line items */}
                  {grouped.bundles.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {grouped.bundles.map((item, idx) => (
                        <Box key={idx} sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontSize: 12, color: COLORS.brown, fontWeight: 600 }}>
                            {item.quantity}x {item.productTitle}
                          </Typography>
                          {item.subItems && item.subItems.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, ml: 1.5, mt: 0.5 }}>
                              {item.subItems.map((sub, sIdx) => (
                                <Chip
                                  key={sIdx}
                                  label={`${sub.quantity}x ${sub.productTitle}`}
                                  size="small"
                                  sx={{ bgcolor: COLORS.orangeLight, fontSize: 10, height: 20 }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {grouped.meals.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                      {grouped.meals.map((item, idx) => (
                        <Chip
                          key={idx}
                          label={`${item.quantity}x ${item.productTitle}`}
                          size="small"
                          sx={{ bgcolor: COLORS.orangeLight, fontSize: 10, height: 20 }}
                        />
                      ))}
                    </Box>
                  )}

                  {grouped.treats.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                      {grouped.treats.map((item, idx) => (
                        <Chip
                          key={idx}
                          label={`${item.quantity}x ${item.productTitle}`}
                          size="small"
                          sx={{ bgcolor: COLORS.greenLight, fontSize: 10, height: 20 }}
                        />
                      ))}
                    </Box>
                  )}

                  {grouped.other.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                      {grouped.other.map((item, idx) => (
                        <Chip
                          key={idx}
                          label={`${item.quantity}x ${item.productTitle}`}
                          size="small"
                          sx={{ bgcolor: QUIZ.color.cream2, fontSize: 10, height: 20 }}
                        />
                      ))}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        )}
      </Collapse>
    </QuizStyledCard>
  );
}
